import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  DayOfWeekEnum,
  ScheduleFrequencyEnum,
  type ScheduleFrequencyMonthly,
  type ScheduleFrequencyWeekly,
  WeekOfMonthEnum,
} from '@ulysses-inc/harami_api_client'
import type { FormikErrors, FormikTouched } from 'formik'
import type React from 'react'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import {
  SCHEDULE_EVERY_MAX,
  SCHEDULE_EVERY_MIN,
} from 'src/features/schedules/scheduleFrequencyLimits'

interface OwnProps {
  frequency: ScheduleFrequencyEnum | null
  frequencyWeekly?: ScheduleFrequencyWeekly
  frequencyMonthly?: ScheduleFrequencyMonthly
  touched: FormikTouched<any>
  submitCount: number
  errors: FormikErrors<any>
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
}

const FrequencySelectOptionInDate: React.FC<OwnProps> = (props: OwnProps) => {
  const daysOfWeek = [
    { value: DayOfWeekEnum.Monday, label: '月' },
    { value: DayOfWeekEnum.Tuesday, label: '火' },
    { value: DayOfWeekEnum.Wednesday, label: '水' },
    { value: DayOfWeekEnum.Thursday, label: '木' },
    { value: DayOfWeekEnum.Friday, label: '金' },
    { value: DayOfWeekEnum.Saturday, label: '土' },
    { value: DayOfWeekEnum.Sunday, label: '日' },
  ]

  const handleDayOfWeekChange = (day: DayOfWeekEnum, checked: boolean) => {
    const currentDays = props.frequencyWeekly?.dayOfWeeks ?? []
    const newDays = checked
      ? [...currentDays, day]
      : currentDays.filter(d => d !== day)
    props.setFieldValue('startReportDate', undefined)
    props.setFieldValue('endReportDate', undefined)
    props.setFieldValue('frequencyWeekly', {
      ...props.frequencyWeekly,
      dayOfWeeks: newDays,
    })
  }

  return (
    <Stack spacing={1}>
      <Select
        value={props.frequency ?? ''}
        onChange={(event: SelectChangeEvent<number>) => {
          const value = event.target.value as number
          props.setFieldValue('frequency', value)
          props.setFieldValue('startReportDate', undefined)
          props.setFieldValue('endReportDate', undefined)
          props.setFieldValue('frequencyWeekly', undefined)
          props.setFieldValue('frequencyMonthly', undefined)
          props.setFieldValue('frequencyCustom', undefined)
          if (value === ScheduleFrequencyEnum.Once)
            props.setFieldValue('visibleEndReportDate', false)
        }}
        size="small"
      >
        <MenuItem value={ScheduleFrequencyEnum.Daily}>毎日</MenuItem>
        <MenuItem value={ScheduleFrequencyEnum.Weekly}>週単位</MenuItem>
        <MenuItem value={ScheduleFrequencyEnum.Monthly}>月単位</MenuItem>
        <MenuItem value={ScheduleFrequencyEnum.Once}>1度だけ</MenuItem>
      </Select>
      {props.submitCount > 0 && (
        <Stack>
          <FormikErrorMessage name="frequency" errors={props.errors} />
        </Stack>
      )}
      {props.frequency === ScheduleFrequencyEnum.Weekly && (
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              type="number"
              value={props.frequencyWeekly?.every ?? ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value
                  ? Number(event.target.value)
                  : undefined
                props.setFieldValue('frequencyWeekly', {
                  ...props.frequencyWeekly,
                  every: value,
                })
              }}
              size="small"
              sx={{ flex: 0.5 }}
              slotProps={{
                htmlInput: {
                  min: SCHEDULE_EVERY_MIN,
                  max: SCHEDULE_EVERY_MAX.weekly,
                },
              }}
            />
            <Typography>週間ごと</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormGroup row>
              {daysOfWeek.map(day => (
                <FormControlLabel
                  key={day.value}
                  control={
                    <Checkbox
                      checked={
                        props.frequencyWeekly?.dayOfWeeks?.includes(
                          day.value,
                        ) ?? false
                      }
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        handleDayOfWeekChange(day.value, event.target.checked)
                      }}
                    />
                  }
                  label={day.label}
                />
              ))}
            </FormGroup>
          </Stack>
          {props.submitCount > 0 && (
            <Stack>
              <FormikErrorMessage
                name="frequencyWeekly.every"
                errors={props.errors}
              />
            </Stack>
          )}
          {props.submitCount > 0 && (
            <Stack>
              <FormikErrorMessage
                name="frequencyWeekly.dayOfWeeks"
                errors={props.errors}
              />
            </Stack>
          )}
        </Stack>
      )}
      {props.frequency === ScheduleFrequencyEnum.Monthly && (
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              type="number"
              value={props.frequencyMonthly?.every ?? ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value
                  ? Number(event.target.value)
                  : undefined
                props.setFieldValue('frequencyMonthly', {
                  ...props.frequencyMonthly,
                  every: value,
                })
              }}
              size="small"
              sx={{ flex: 0.5 }}
              slotProps={{
                htmlInput: {
                  min: SCHEDULE_EVERY_MIN,
                  max: SCHEDULE_EVERY_MAX.monthly,
                },
              }}
            />
            <Typography>ヵ月ごと</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Select
              value={props.frequencyMonthly?.weekOfMonth ?? ''}
              onChange={(event: SelectChangeEvent<number>) => {
                const value = event.target.value as number
                props.setFieldValue('startReportDate', undefined)
                props.setFieldValue('endReportDate', undefined)
                props.setFieldValue('frequencyMonthly', {
                  ...props.frequencyMonthly,
                  weekOfMonth: value,
                })
              }}
              size="small"
              sx={{ flex: 1 }}
            >
              <MenuItem value={WeekOfMonthEnum.First}>最初の</MenuItem>
              <MenuItem value={WeekOfMonthEnum.Second}>第2</MenuItem>
              <MenuItem value={WeekOfMonthEnum.Third}>第3</MenuItem>
              <MenuItem value={WeekOfMonthEnum.Fourth}>第4</MenuItem>
              <MenuItem value={WeekOfMonthEnum.Last}>最終</MenuItem>
            </Select>
            <Select
              value={props.frequencyMonthly?.dayOfWeek ?? ''}
              onChange={(event: SelectChangeEvent<number>) => {
                const value = event.target.value as number
                props.setFieldValue('startReportDate', undefined)
                props.setFieldValue('endReportDate', undefined)
                props.setFieldValue('frequencyMonthly', {
                  ...props.frequencyMonthly,
                  dayOfWeek: value,
                })
              }}
              size="small"
              sx={{ flex: 1 }}
            >
              <MenuItem value={DayOfWeekEnum.Monday}>月曜日</MenuItem>
              <MenuItem value={DayOfWeekEnum.Tuesday}>火曜日</MenuItem>
              <MenuItem value={DayOfWeekEnum.Wednesday}>水曜日</MenuItem>
              <MenuItem value={DayOfWeekEnum.Thursday}>木曜日</MenuItem>
              <MenuItem value={DayOfWeekEnum.Friday}>金曜日</MenuItem>
              <MenuItem value={DayOfWeekEnum.Saturday}>土曜日</MenuItem>
              <MenuItem value={DayOfWeekEnum.Sunday}>日曜日</MenuItem>
            </Select>
          </Stack>
          {props.submitCount > 0 && (
            <Stack>
              <FormikErrorMessage
                name="frequencyMonthly.every"
                errors={props.errors}
              />
            </Stack>
          )}
          {props.submitCount > 0 && (
            <Stack>
              <FormikErrorMessage
                name="frequencyMonthly.weekOfMonth"
                errors={props.errors}
              />
            </Stack>
          )}
          {props.submitCount > 0 && (
            <Stack>
              <FormikErrorMessage
                name="frequencyMonthly.dayOfWeek"
                errors={props.errors}
              />
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default FrequencySelectOptionInDate
